/**
 * MorphViewerPage — Replaces GestureViewer as the default shop viewer.
 *
 * Architecture: morph-viewer-v2.html runs in a fullscreen <iframe>.
 * React overlays sit on top and communicate via postMessage:
 *
 *   iframe → React:
 *     {type:'frame-change', frameIndex}   — frame navigated
 *     {type:'tap', imagePixelX, imagePixelY, frameIndex} — user tapped (disk handled by React)
 *     {type:'mark-item', item}            — user marked an item inside iframe
 *     {type:'ready'}                      — iframe finished loading
 *
 *   React → iframe:
 *     {type:'navigate-to-frame', frameIndex}    — orbit step
 *     {type:'section-info', name, index, total} — section switch
 *
 * Preserved features (unchanged React components):
 *   - FrameItemMarkers  (marked items with 3D projection)
 *   - TapCameraIndicator  (disk blocker + camera discovery + orbit)
 *   - SectionsDrawer  (section/room switching)
 */

import { useEffect, useState, useRef, useCallback, useMemo } from 'react'
import { useAppStore } from '@store/appStore'
import { useAuthStore, getAuthHeaders } from '@/stores/authStore'
import { buildMorphUrl, buildMorphUrlFromBase } from '@/utils/buildMorphUrl'
import { buildOrbitSequence } from '@/utils/orbitNavigation'
import { calculateCameraVisibility } from '@/utils/cameraProjection'
import { calculateIndicatorPosition } from './calculateIndicatorPosition'
import { useMarkItem } from '@/hooks/useMarkItem'
import { useCartStore } from '@/stores/cartStore'
import { useStableOffsetMap } from '@/hooks/useConnectedFrames'
import { useDepthViewer, type ViewerKind, markDepthAvailable, markCloudAvailable } from '@/hooks/useDepthViewer'
import { useSectionGraph } from '@/hooks/useSectionGraph'
import { SectionChip } from './SectionChip'
import { DepthGeneratePrompt } from '@/features/depth/DepthGeneratePrompt'
import { ShopReviewDrawer } from '@/components/Shops/ShopReviewDrawer'
import { ShopInfoDrawer } from './ShopInfoDrawer'
import { ChatBalloon } from './ChatBalloon'
import { AskPinOverlay } from './AskPinOverlay'
import { TapCameraIndicator } from './TapCameraIndicator'
import { FrameItemMarkers } from './FrameItemMarkers'
import { SectionsDrawer } from '@/components/SectionsDrawer/SectionsDrawer'
import { LoadingProgressBar } from '@/components/LoadingProgressBar/LoadingProgressBar'
import { ItemListPanel } from '@/features/delivery/components/ItemListPanel'
import { CartDrawer } from '@/features/delivery/components/CartDrawer'
import { apiUrl, authFetch } from '@/config'
import { getGuestId } from '@/utils/guestIdentity'
import { downloadSectionFrames, isLocalHost } from '@/utils/downloadSectionFrames'
import { buildSyntheticOffsetMapFromManifest } from '@/utils/offsetMapUtils'
import type { TapIndicatorState } from '@/types/TapIndicatorTypes'
import './GestureViewer.css'

interface MorphViewerPageProps {
  shopId: string
  /** Pre-built morph viewer URL (for uploaded scans). When provided, skips dataset loading. */
  directMorphUrl?: string
}

export function MorphViewerPage({ shopId, directMorphUrl }: MorphViewerPageProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [morphUrl, setMorphUrl] = useState<string | null>(null)
  const manifestUrlRef = useRef<string | null>(null)

  // When a previously-set morphUrl differs from an incoming one ONLY by its SAS
  // token (base identical), swapping `src` reloads the iframe and aborts any
  // in-flight fetches — producing the "[MorphViewer] Network error loading JSON:
  // TypeError: Failed to fetch" we saw when Step 2 and Step 2b each fetched a
  // fresh SAS. This setter treats same-base as a no-op so the iframe keeps
  // using whichever SAS it already has (24h validity — plenty of headroom).
  const _extractBase = (u: string | null): string | null => {
    if (!u) return null
    try {
      const q = u.split('?')[1]
      if (!q) return null
      return new URLSearchParams(q).get('base')
    } catch { return null }
  }
  const setMorphUrlIfBaseChanged = useCallback((next: string | null) => {
    setMorphUrl((prev) => {
      if (!prev || !next) return next
      const a = _extractBase(prev)
      const b = _extractBase(next)
      if (a && b && a === b) return prev
      return next
    })
  }, [])

  const loadManifestForMobileShop = useCallback(async (manifestUrl: string) => {
    try {
      // Try .gz first (replace .json→.json.gz before any query string)
      const gzUrl = manifestUrl.replace('.json', '.json.gz')
      let manifest: any = null
      const gzRes = await fetch(gzUrl)
      if (gzRes.ok) {
        manifest = await gzRes.json()
      } else {
        const res = await fetch(manifestUrl)
        if (res.ok) manifest = await res.json()
      }
      if (!manifest) return
      const offsetMap = buildSyntheticOffsetMapFromManifest(manifest)
      if (offsetMap) {
        useAppStore.setState({ offsetMap })
      }
    } catch (e) {
      console.warn('[MorphViewerPage] manifest load failed:', e)
    }
  }, [])
  // Which viewer this section gets. `morphUrl` stays exactly as it was and remains the single
  // source of the scan's location; the depth viewer is the same location on a different page,
  // re-resolved per section. See useDepthViewer.
  const { viewerUrl, viewerKind, available, setViewerKind, scanLocation, scanIds,
          refreshDepth } = useDepthViewer(morphUrl)
  const [depthPromptOpen, setDepthPromptOpen] = useState(false)

  const [currentFrameIndex, setCurrentFrameIndex] = useState(0)
  /**
   * How many frames the open section has, as the VIEWER counts them.
   *
   * Not `cameras.length`: the depth viewer publishes empty cameras on purpose — it is
   * self-contained and has no COLMAP data to share — so in the default viewer that array is
   * always 0. The `ready` message carries the real count, and both viewers send it.
   */
  const [viewerFrameCount, setViewerFrameCount] = useState(0)
  const [tapIndicator, setTapIndicator] = useState<TapIndicatorState | null>(null)
  const [tapIndicatorPannedPosition, setTapIndicatorPannedPosition] = useState<{ x: number; y: number } | null>(null)
  const visitNotifiedRef = useRef(false)
  const directShopLoadedRef = useRef(false)
  const [reviewOpen, setReviewOpen] = useState(false)
  const [infoOpen, setInfoOpen] = useState(false)
  const [chatOpen, setChatOpen] = useState(false)
  const [zenMode, setZenMode] = useState(false)
  const authUser = useAuthStore((s) => s.user)

  // "Ask about this" pin-placement flow
  const [askPin, setAskPin] = useState<{ x: number; y: number; r: number } | null>(null)
  const [chatPin, setChatPin] = useState<
    { frameIndex: number; imagePixelX: number; imagePixelY: number; radius: number; sectionIndex: number }
    | null
  >(null)

  // Asset deep-link from Messages chip: ?frame=N&px=...&py=...&pr=...
  // Persists across re-renders so we can navigate the iframe to that frame
  // on first 'ready' and show a non-interactive green circle on top.
  const assetTargetRef = useRef<{ frame: number | null; px: number | null; py: number | null; pr: number | null } | null>(null)
  const [assetPin, setAssetPin] = useState<{ frame: number; px: number; py: number; pr: number } | null>(null)
  // Parse URL once on mount
  useEffect(() => {
    const u = new URLSearchParams(window.location.search)
    const frame = u.get('frame') != null ? parseInt(u.get('frame')!, 10) : null
    const px = u.get('px') != null ? parseFloat(u.get('px')!) : null
    const py = u.get('py') != null ? parseFloat(u.get('py')!) : null
    const pr = u.get('pr') != null ? parseFloat(u.get('pr')!) : null
    if (px != null && py != null) {
      assetTargetRef.current = { frame, px, py, pr }
      if (frame != null) setAssetPin({ frame, px, py, pr: pr ?? 40 })
    }
  }, [])


  // Store selectors
  const cameras = useAppStore((s) => s.cameras)
  const sections = useAppStore((s) => s.sections)
  const shopDatasets = useAppStore((s) => s.shopDatasets)
  const canUseTapIndicator = useAppStore((s) => s.canUseTapIndicator)
  const isLoadingData = useAppStore((s) => s.isLoadingData)
  const dataError = useAppStore((s) => s.dataError)
  const loadingProgress = useAppStore((s) => s.loadingProgress)
  const loadingStage = useAppStore((s) => s.loadingStage)
  const currentDatasetId = useAppStore((s) => s.currentDatasetId)
  const currentSectionFramesPath = useAppStore((s) => s.currentSectionFramesPath)
  const manuallySelectedFrameIndex = useAppStore((s) => s.manuallySelectedFrameIndex)
  const loadDataset = useAppStore((s) => s.loadDataset)
  const availableDatasets = useAppStore((s) => s.availableDatasets)

  // 3D offset map for camera ranking
  const rawOffsetMap = useAppStore((s) => s.offsetMap)
  const offsetMap = useStableOffsetMap(rawOffsetMap)

  // Marked items hook + panel state
  const { handleMarkItem, itemListOpen, setItemListOpen, cartDrawerOpen, setCartDrawerOpen } = useMarkItem({ shopId })

  // ── Sections as one connected place ─────────────────────────────────────────
  // The arrangement of the shop's rooms, and the crossing between them. See useSectionGraph:
  // no scan data relates two sections, so the owner's `order` is what "next room" means.
  const sectionGraph = useSectionGraph()
  const [drawerOpenSignal, setDrawerOpenSignal] = useState(0)
  const [crossing, setCrossing] = useState<{ poster: string; name: string } | null>(null)
  const crossingRef = useRef(false)
  /**
   * Which frame the section now loading should open on.
   *
   * A ref consumed by the `ready` handler rather than an option on the load: the section switch
   * replaces the iframe, and a brand new document starts at frame 0 whatever the store says. The
   * only moment it can be told otherwise is when it announces itself.
   */
  const pendingLandingRef = useRef<number | null>(null)

  // Count only items for the current shop + section (matches what ItemListPanel shows)
  const currentSectionId = useAppStore((s) => s.currentSectionId)
  const allCartItems = useCartStore((s) => s.items)
  const visibleItemCount = allCartItems.filter(
    (i) => i.shopId === shopId && i.sectionId === String(currentSectionId)
  ).length

  // Current frame camera
  const frameCamera = cameras[currentFrameIndex] ?? cameras[0]

  // ── Dev-only: download every frame of the section currently open ─────────────
  const showFrameDownload = isLocalHost()
  const [frameDownload, setFrameDownload] = useState<{ done: number; total: number } | null>(null)

  const handleDownloadFrames = useCallback(async () => {
    if (frameDownload) return // already running
    setFrameDownload({ done: 0, total: 0 })
    try {
      const result = await downloadSectionFrames({
        morphUrl,
        frameFiles: cameras.map((c) => c.frame_file).filter(Boolean),
        fileName: `${shopId}_section-${currentSectionId ?? '0'}_frames`,
        onProgress: (done, total) => setFrameDownload({ done, total }),
      })
      console.log(
        `[MorphViewerPage] downloaded ${result.downloaded} frames ` +
        `(${(result.bytes / 1024 / 1024).toFixed(1)} MB, ${result.failed} skipped)`
      )
      if (result.failed > 0) {
        alert(`Downloaded ${result.downloaded} frames — ${result.failed} could not be fetched.`)
      }
    } catch (e) {
      console.error('[MorphViewerPage] frame download failed:', e)
      alert(`Frame download failed: ${(e as Error).message}`)
    } finally {
      setFrameDownload(null)
    }
  }, [frameDownload, morphUrl, cameras, shopId, currentSectionId])

  // Display dimensions (full viewport since iframe is fullscreen)
  const displayWidth = window.innerWidth
  const displayHeight = window.innerHeight

  // ── Load a mobile-uploaded shop via public API + blob URLs ──────────────────
  //
  // Stage-1 fast-pan: we commit shop metadata + morph URL as soon as the
  // public-shop response arrives. The iframe itself fetches cameras.json /
  // points3d.json and shares the parsed data back via the `scan-data-ready`
  // postMessage (handled below) — so React never fetches those files.
  const loadMobileShop = useCallback(async (mobileShopId: string, base: string, sas: string) => {
    useAppStore.getState().setLoadingData(true)
    try {
      const shopRes = await authFetch(apiUrl(`/api/shops/public/${encodeURIComponent(mobileShopId)}`))
      if (!shopRes.ok) { directShopLoadedRef.current = true; useAppStore.getState().setLoadingData(false); return }
      const shopData = await shopRes.json()
      // `order` is what the shop owner arranged the rooms into, and the only cross-section
      // relation that exists anywhere — dropping it here (as the narrower annotation used to)
      // left the viewer sorting rooms by camera count, which is not an arrangement at all.
      const remoteSections: Array<{
        sectionId: string; name?: string; order?: number; posterFrameIndex?: number
      }> = shopData?.sections ?? []

      let effectiveSas = sas
      if (!effectiveSas) {
        try {
          const sasRes = await authFetch(apiUrl('/api/scan-frames-token'))
          if (sasRes.ok) {
            const sasData = await sasRes.json()
            effectiveSas = sasData.sasToken ?? sasData.sas_token ?? ''
          }
        } catch { /* ignore */ }
      }

      const appendSas = (url: string) => effectiveSas ? `${url}${url.includes('?') ? '&' : '?'}${effectiveSas}` : url
      const getSecBase = (sec: any) => {
        if (sec.camerasJsonUrl) {
          return sec.camerasJsonUrl.substring(0, sec.camerasJsonUrl.lastIndexOf('/') + 1)
        }
        return `https://vshopship.blob.core.windows.net/scan-frames/${mobileShopId}/${sec.sectionId}/v1/`
      }

      const shopDatasets = remoteSections.map((sec, i) => {
        const secBase = getSecBase(sec)
        return {
          id: `${mobileShopId}_${sec.sectionId}`,
          shopId: mobileShopId,
          sectionId: sec.sectionId,
          sectionName: sec.name ?? `Section ${i + 1}`,
          order: sec.order,
          posterFrameIndex: sec.posterFrameIndex,
          name: sec.name ?? `Section ${i + 1}`,
          video: '', experiment: '', reconstruction_id: '', camera_count: 0,
          success_rate: '', quality_score: 0, description: '', matcher: '', resolution: '', fps: 0,
          paths: {
            cameras: appendSas(`${secBase}cameras.json`),
            points3d: appendSas(`${secBase}points3d.json`),
            points3d_simplified: appendSas(`${secBase}points3d.json`),
            frames: appendSas(`${secBase}frames/`),
            manifest: appendSas(`${secBase}manifest.json`),
          },
          source: { video_path: '', colmap_output: '' },
        }
      })

      remoteSections.forEach((sec: any) => {
        const sBase = getSecBase(sec)
        if (sec.depthV2 || sec.depthV2Meta?.status === 'ready') {
          markDepthAvailable(sBase)
        }
        if (sec.depthWithColmap || sec.depthWithColmapMeta?.status === 'ready') {
          markCloudAvailable(sBase)
        }
      })

      // Store first section's manifest URL so scan-data-ready can load it
      if (remoteSections[0]) {
        const firstBase = getSecBase(remoteSections[0])
        manifestUrlRef.current = appendSas(`${firstBase}manifest.json`)
      }

      // Use the provided base (from directMorphUrl), or fall back to the first section's base
      const firstSection = remoteSections[0]
      const resolvedBase = base || (firstSection ? getSecBase(firstSection) : '')

      // Build the morph URL; the iframe will handle cameras/points fetching.
      if (resolvedBase) {
        const morphParams = new URLSearchParams({ base: resolvedBase, ...(effectiveSas ? { sas: effectiveSas } : {}) })
        setMorphUrl(`/morph-viewer/morph-viewer-v2.html?${morphParams.toString()}`)
      }

      directShopLoadedRef.current = true
      useAppStore.setState({
        currentDatasetId: mobileShopId,
        currentShopId: mobileShopId,
        shopDatasets,
        activeUserSectionId: firstSection?.sectionId ?? null,
        isLoadingData: false,
        // cameras/points3D populated by the `scan-data-ready` bridge below.
      })
    } catch (e) {
      console.error('[MorphViewerPage] loadMobileShop failed:', e)
      useAppStore.getState().setLoadingData(false)
    }
  }, [])

  // ── Step 0: Never inherit, never leave behind, another shop's scan ────────────
  //
  // The store outlives this component: shops are switched without a page reload, so
  // whatever shop was open last is still in the store when the next one mounts. Left
  // alone, its cameras, sections and section paths are what the viewer renders for the
  // first paint of the new shop — the previous shop showing before the new one loads.
  //
  // So the scan is cleared on the way out as well as on the way in. Clearing on exit is
  // what makes it impossible: leaving the viewer at all — to another shop, home, browse
  // — puts the store back to "no shop open", with no chance for the next mount to paint
  // someone else's shop first.
  //
  // Mount-only: App keys this component by the shop in the URL, so it runs once per shop.
  useEffect(() => {
    if (directMorphUrl || !shopId) return
    const clearLoadedScan = () => useAppStore.setState({
      cameras: [],
      points3D: [],
      sections: [],
      shopDatasets: [],
      offsetMap: null,
      canUseTapIndicator: false,
      currentSectionFramesPath: null,
      currentDatasetId: null,
      currentShopId: null,
    })
    const { currentDatasetId: loadedDatasetId, availableDatasets: ads } = useAppStore.getState()
    // `loadedDatasetId` is a resolved dataset id, so a shop DB id in the URL matches it
    // through the dataset's own shopId — otherwise re-entering a shop would throw away
    // data that is still good.
    const loadedBelongsToShop = !loadedDatasetId || loadedDatasetId === shopId ||
      ads.find((d) => d.id === loadedDatasetId)?.shopId === shopId
    if (!loadedBelongsToShop) clearLoadedScan()
    return clearLoadedScan
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ── Step 1: Load dataset into Zustand store ──────────────────────────────────
  useEffect(() => {
    // ── directMorphUrl path (legacy ?morphUrl= format) ──
    if (directMorphUrl) {
      const urlParams = new URLSearchParams(directMorphUrl.split('?')[1] ?? '')
      const base = urlParams.get('base') ?? ''
      const sas = urlParams.get('sas') ?? ''
      const extractedShopId = base.match(/scan-frames\/(shop_[^/]+)\//)?.[1]
      setMorphUrl(directMorphUrl)
      if (!extractedShopId || directShopLoadedRef.current) return
      loadMobileShop(extractedShopId, base, sas)
      return
    }

    if (!shopId || shopId === currentDatasetId) return

    // ── shopId path: check if it's in the manifest first ──
    const { availableDatasets: ads } = useAppStore.getState()
    const inManifest = ads.some(d => d.id === shopId || d.shopId === shopId)
    if (inManifest || ads.length === 0) {
      // Standard manifest-based shop OR datasets not loaded yet — use loadDataset
      const urlParams = new URLSearchParams(window.location.search)
      const targetSection = urlParams.get('section') ? parseInt(urlParams.get('section')!, 10) : undefined
      const targetFrame = urlParams.get('frame') ? parseInt(urlParams.get('frame')!, 10) : undefined
      useAppStore.getState().setLoadingData(true)
      loadDataset(shopId, { section: targetSection, frame: targetFrame })
    } else {
      // Mobile-uploaded shop: not in manifest — use public shop API + blob URLs
      if (directShopLoadedRef.current) return
      loadMobileShop(shopId, '', '')
    }
  }, [shopId, currentDatasetId, loadDataset, directMorphUrl, availableDatasets, loadMobileShop])

  // ── Step 2: Build morph iframe URL from dataset manifest ─────────────────────
  useEffect(() => {
    if (directMorphUrl || !shopId) return
    const dataset = availableDatasets.find((d) => d.id === shopId)
    // A shop's own DB id owns no dataset — loadDataset resolves it to the shop's best
    // section, and only that section's base exists. Guessing `<id>/section_0/` here asks
    // for a blob that isn't there (the cameras.json 404s) and shows that dead base until
    // Step 2b replaces it, so for a resolvable id the first URL is left to Step 2b.
    if (!dataset && availableDatasets.some((d) => d.shopId === shopId)) return
    buildMorphUrl(dataset ?? { id: shopId })
      .then(setMorphUrlIfBaseChanged)
      .catch(() => {
        setMorphUrlIfBaseChanged(`/morph-viewer/morph-viewer-v2.html?base=${encodeURIComponent(`/api/scan-data/${shopId}/section_0/`)}`)
      })
  }, [shopId, availableDatasets, directMorphUrl, setMorphUrlIfBaseChanged])

  // ── Step 2b: Rebuild morph URL from the correct section after loadDataset ────
  useEffect(() => {
    if (directMorphUrl || !currentSectionFramesPath || currentDatasetId !== shopId) return
    const framesDir = currentSectionFramesPath.endsWith('/frames/')
      ? currentSectionFramesPath.slice(0, -'frames/'.length)
      : currentSectionFramesPath.replace(/\/frames\/?$/, '/')
    buildMorphUrlFromBase(framesDir)
      .then(setMorphUrlIfBaseChanged)
      .catch(console.error)
  }, [currentSectionFramesPath, currentDatasetId, shopId, directMorphUrl, setMorphUrlIfBaseChanged])

  // A new URL means a new iframe (see the key on it), and a fresh document opens on its
  // first frame — so React's idea of where the viewer is has to follow it back to 0, or
  // the overlays keep projecting onto a frame that is no longer on screen.
  useEffect(() => {
    setCurrentFrameIndex(0)
    setViewerFrameCount(0)
    setTapIndicator(null)
    setTapIndicatorPannedPosition(null)
  }, [viewerUrl])

  // ── Step 3: Track shop visit (same as GestureViewer) ─────────────────────────
  useEffect(() => {
    if (!shopId || !currentDatasetId || visitNotifiedRef.current) return
    visitNotifiedRef.current = true
    const authUser = useAuthStore.getState().user
    const visitorId = authUser?.id || getGuestId()
    const visitorName = authUser?.name || undefined
    fetch(apiUrl(`/api/shops/${encodeURIComponent(shopId)}/visit`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify({ visitorId, visitorName }),
    }).catch(() => {})
    import('../../services/activityService').then(({ trackVisit }) => { trackVisit(shopId) })
  }, [shopId, currentDatasetId])

  // ── Step 4: postMessage bridge ────────────────────────────────────────────────
  const sendToIframe = useCallback((msg: object) => {
    try {
      iframeRef.current?.contentWindow?.postMessage(JSON.stringify(msg), '*')
    } catch { /* ignore */ }
  }, [])

  /**
   * Cross into another section — from a doorway push, or from the chip's chevrons.
   *
   * The poster goes up BEFORE the load starts and comes down only when it finishes. Switching
   * sections rebuilds the viewer URL, which changes the iframe's key and tears the whole
   * document down; without something over the top, the crossing is a black flash. `warmStartFrame`
   * is set for the same reason on the other viewers that read it.
   *
   * `skipAnimation` matters as much: both switch paths otherwise start playAllFramesInSection,
   * a 60-step auto-walkthrough at a second a step. Seizing the camera for a minute is a strange
   * answer to someone who just walked through a door under their own steam.
   */
  const crossToSection = useCallback((key: string) => {
    if (crossingRef.current) return
    const targetIndex = sectionGraph.ordered.findIndex((n) => n.key === key)
    const target = sectionGraph.ordered[targetIndex]
    if (!target || target.isActive) return
    const store = useAppStore.getState()

    // Sections join END TO START. Walking forward you arrive at the next room's first frame;
    // walking back you arrive at the previous room's LAST one, because you came in through its
    // far end. Landing on frame 0 either way would mean stepping backwards through a door and
    // finding yourself at the start of the room you were meant to be returning into.
    const goingBack = sectionGraph.currentIndex >= 0 && targetIndex < sectionGraph.currentIndex
    pendingLandingRef.current = goingBack && target.frameCount > 1 ? target.frameCount - 1 : 0

    // Two kinds of section, two different ways to load one. The graph carries which — see
    // useSectionGraph; a COLMAP component and a room the owner named are switched by different
    // store actions and are not interchangeable.
    let load: Promise<void>
    if (target.kind === 'user') {
      load = store.loadSectionDataset(target.userSectionId!, { skipAnimation: true })
    } else if (shopId) {
      // No skipAnimation to suppress here: loadSectionAsIndependentShop does not autoplay on its
      // own — the drawer calls playAllFramesInSection itself afterwards, and we simply do not.
      load = store.loadSectionAsIndependentShop(shopId, target.colmapSectionId!)
    } else {
      return
    }

    crossingRef.current = true
    store.cancelFrameSequenceAnimation?.()
    store.setWarmStartFrame(target.posterUrl || null)
    setCrossing({ poster: target.posterUrl, name: target.name })
    load
      .catch((err: Error) => console.error('[MorphViewerPage] section crossing failed:', err))
      .finally(() => {
        crossingRef.current = false
        setCrossing(null)
      })
  }, [sectionGraph, shopId])

  useEffect(() => {
    const handler = (e: MessageEvent) => {
      if (e.source !== iframeRef.current?.contentWindow) return
      let msg: any
      try { msg = typeof e.data === 'string' ? JSON.parse(e.data) : e.data } catch { return }

      switch (msg.type) {
        case 'ready': {
          if (typeof msg.frameCount === 'number') setViewerFrameCount(msg.frameCount)
          // A fresh document has missed every message sent before it, and the neighbours are
          // what make its walls doors.
          sendToIframe(neighborsMessageRef.current)

          // Arriving through a doorway. Walking back into a room puts you at its far end, which
          // is the frame you would have been standing on had you never left it.
          const landing = pendingLandingRef.current
          pendingLandingRef.current = null
          if (landing) {
            sendToIframe({ type: 'navigate-to-frame', frameIndex: landing })
            setCurrentFrameIndex(landing)
          }

          // Asset deep-link: hop straight to the asked frame. Deliberately after the landing —
          // an explicit link to one shelf beats a default arrival.
          const target = assetTargetRef.current
          if (target?.frame != null) {
            iframeRef.current?.contentWindow?.postMessage(
              JSON.stringify({ type: 'navigate-to-frame', frameIndex: target.frame }),
              '*',
            )
            setCurrentFrameIndex(target.frame)
          }
          break
        }

        case 'scan-data-ready': {
          // Iframe finished parsing cameras.json. Read the parsed data across
          // the same-origin iframe boundary. points3d arrives later via tap-ready.
          try {
            const scanData = (iframeRef.current?.contentWindow as unknown as { __scanData?: { cameras: unknown[]; points: unknown[] } } | null)?.__scanData
            if (!scanData?.cameras?.length) break
            useAppStore.setState({
              cameras: scanData.cameras as never,
              points3D: (scanData.points ?? []) as never,
              // canUseTapIndicator set later when tap-ready arrives (points3d loaded)
              isLoadingData: false,
            })
            // Load manifest for mobile-uploaded scans so offsetMap is available
            // for tap indicator and frame filtering (COLMAP datasets load it via appStore)
            if (manifestUrlRef.current) {
              loadManifestForMobileShop(manifestUrlRef.current)
            }
          } catch { /* ignore */ }
          break
        }

        case 'tap-ready': {
          // points3d.json finished loading in the iframe — enable tap indicator
          useAppStore.setState({ canUseTapIndicator: true })
          break
        }

        case 'frame-change':
          if (typeof msg.frameIndex === 'number') {
            setCurrentFrameIndex(msg.frameIndex)
            setTapIndicator(null) // dismiss disk on manual navigation
          }
          break

        case 'tap': {
          if (!canUseTapIndicator || !cameras.length) break
          const { imagePixelX, imagePixelY, frameIndex: tapFrame = currentFrameIndex } = msg
          const cam = cameras[tapFrame] ?? cameras[0]
          if (!cam) break

          const imgW = cam.intrinsics.width
          const imgH = cam.intrinsics.height

          // Use image dimensions as the container rect (same pattern as NewModeViewer shared URL tap)
          const fakeRect = new DOMRect(0, 0, imgW, imgH)
          const points3D = useAppStore.getState().points3D
          const { highlightedCameras, projections, point3D } = calculateCameraVisibility(
            imagePixelX, imagePixelY, fakeRect, cam, cameras, points3D, offsetMap, tapFrame
          )

          // Scale image-space coords to display coords
          const displayX = (imagePixelX / imgW) * displayWidth
          const displayY = (imagePixelY / imgH) * displayHeight

          // Build orbit sequence
          if (point3D) {
            const orbit = buildOrbitSequence(point3D, projections, cameras, tapFrame)
            const store = useAppStore.getState()
            store.setOrbitSequence?.(orbit)
            store.setOrbitIndex?.(0)
          }

          setTapIndicatorPannedPosition(null)
          setTapIndicator({
            show: true,
            cameraCount: highlightedCameras.size,
            screenPosition: { x: displayX, y: displayY },
            frameRelativeTapX: imagePixelX,
            frameRelativeTapY: imagePixelY,
            imagePixelX,
            imagePixelY,
            currentDisplayX: displayX,
            currentDisplayY: displayY,
            projections,
            point3D,
            frameCamera: cam,
            frameIndex: tapFrame,
            tappedFrameIndex: tapFrame,
            is2DOnly: !point3D,
          })
          break
        }

        case 'zen-mode':
          setZenMode(!!msg.active)
          break

        case 'section-step': {
          // The user pushed through a doorway in the depth viewer. It sends a POSITION in our
          // arrangement, not a section id — which room lies behind that wall is our fact, not
          // the viewer's, and it only ever knew the name and poster we handed it.
          const target = msg.dir === 'prev' ? sectionGraph.prev : sectionGraph.next
          if (target) crossToSection(target.key)
          break
        }

        case 'mark-item': {
          // Morph viewer marked an item natively — save to CartStore
          if (msg.item) {
            const { imagePixelX, imagePixelY, frameIndex, point3D } = msg.item
            handleMarkItem({
              imagePixelX: imagePixelX ?? 0,
              imagePixelY: imagePixelY ?? 0,
              radius: 30,
              point3D: point3D ?? null,
              projections: new Map(),
              frameIndex: frameIndex ?? currentFrameIndex,
            })
          }
          break
        }
      }
    }
    window.addEventListener('message', handler)
    return () => window.removeEventListener('message', handler)
  }, [cameras, canUseTapIndicator, currentFrameIndex, displayWidth, displayHeight, offsetMap, handleMarkItem, sectionGraph, crossToSection])

  // ── Tell the viewer which rooms adjoin this one ───────────────────────────────
  //
  // The depth viewer paints the next room's poster into the wall you push against, and it cannot
  // work any of this out for itself: it reads one section directory and nothing above it. Sent on
  // every change AND on `ready`, because a fresh iframe has missed everything sent before it.
  const neighborsMessage = useMemo(() => ({
    type: 'section-neighbors',
    next: sectionGraph.next ? { name: sectionGraph.next.name, posterUrl: sectionGraph.next.posterUrl } : null,
    prev: sectionGraph.prev ? { name: sectionGraph.prev.name, posterUrl: sectionGraph.prev.posterUrl } : null,
  }), [sectionGraph.next, sectionGraph.prev])

  // Read by the `ready` handler, which must not be re-subscribed every time the graph changes.
  const neighborsMessageRef = useRef(neighborsMessage)
  neighborsMessageRef.current = neighborsMessage

  useEffect(() => {
    if (!neighborsMessage.next && !neighborsMessage.prev) return
    sendToIframe(neighborsMessage)
  }, [neighborsMessage, viewerUrl, sendToIframe])

  // ── Orbit helpers ─────────────────────────────────────────────────────────────
  const handleOrbitStep = useCallback((targetFrameIndex: number) => {
    sendToIframe({ type: 'navigate-to-frame', frameIndex: targetFrameIndex })
    setCurrentFrameIndex(targetFrameIndex)
    // Update tap indicator position for new frame
    if (tapIndicator) {
      const cam = cameras[targetFrameIndex]
      if (!cam) return
      const proj = tapIndicator.projections.get(targetFrameIndex)
      if (!proj) return
      const [dx, dy] = calculateIndicatorPosition(proj, cam.intrinsics.width, cam.intrinsics.height, displayWidth, displayHeight)
      setTapIndicator((prev) => prev ? { ...prev, frameIndex: targetFrameIndex, frameCamera: cam, currentDisplayX: dx, currentDisplayY: dy } : null)
      setTapIndicatorPannedPosition(null)
    }
  }, [sendToIframe, tapIndicator, cameras, displayWidth, displayHeight])

  const handleOrbitPrev = useCallback(() => {
    const store = useAppStore.getState()
    const seq = store.orbitSequence
    const idx = store.orbitIndex
    if (!seq || seq.length < 2) return
    const prevIdx = (idx - 1 + seq.length) % seq.length
    store.setOrbitIndex(prevIdx)
    handleOrbitStep(seq[prevIdx].frameIndex)
  }, [handleOrbitStep])

  const handleOrbitNext = useCallback(() => {
    const store = useAppStore.getState()
    const seq = store.orbitSequence
    const idx = store.orbitIndex
    if (!seq || seq.length < 2) return
    const nextIdx = (idx + 1) % seq.length
    store.setOrbitIndex(nextIdx)
    handleOrbitStep(seq[nextIdx].frameIndex)
  }, [handleOrbitStep])

  // ── Forward ItemListPanel frame navigation to iframe ─────────────────────────
  useEffect(() => {
    if (manuallySelectedFrameIndex == null) return
    sendToIframe({ type: 'navigate-to-frame', frameIndex: manuallySelectedFrameIndex })
    setCurrentFrameIndex(manuallySelectedFrameIndex)
  }, [manuallySelectedFrameIndex, sendToIframe])

  // ── Desktop arrow key navigation (when iframe doesn't have focus) ─────────────
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        sendToIframe({ type: 'keydown', key: e.key })
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [sendToIframe])

  // ── TapCameraIndicator derived values ─────────────────────────────────────────
  const indicatorDisplayX = tapIndicator ? calculateIndicatorPosition(
    [tapIndicator.imagePixelX, tapIndicator.imagePixelY],
    frameCamera?.intrinsics?.width ?? 450,
    frameCamera?.intrinsics?.height ?? 800,
    displayWidth,
    displayHeight,
  )[0] : 0

  const indicatorDisplayY = tapIndicator ? calculateIndicatorPosition(
    [tapIndicator.imagePixelX, tapIndicator.imagePixelY],
    frameCamera?.intrinsics?.width ?? 450,
    frameCamera?.intrinsics?.height ?? 800,
    displayWidth,
    displayHeight,
  )[1] : 0

  const indicatorPosition = tapIndicatorPannedPosition ?? { x: indicatorDisplayX, y: indicatorDisplayY }

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="gesture-viewer" style={{ position: 'fixed', inset: 0, background: '#000' }}>
      {/* Loading state while the viewer URL is being resolved. A shop whose dataset fails
          to resolve now has no guessed URL to fall back on (see Step 2), so say so rather
          than spin forever. */}
      {!viewerUrl && (
        <div className="gesture-viewer__loading">
          <button
            className="gesture-viewer__cancel-btn"
            onClick={() => window.location.href = '/'}
            title="Cancel"
          >✕</button>
          {dataError ? (
            <p>This shop could not be opened.</p>
          ) : (
            <>
              <div className="spinner" />
              <p>Loading shop...</p>
            </>
          )}
        </div>
      )}

      {/* Section-switch progress bar */}
      {isLoadingData && canUseTapIndicator && (
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 500, pointerEvents: 'none' }}>
          <LoadingProgressBar progress={loadingProgress} stage={loadingStage || 'Loading section...'} />
        </div>
      )}

      {/* ── The shop viewer iframe — v1 morph or v2 depth, same postMessage contract ──
           key includes the URL on purpose. Assigning `src` to an iframe that has already
           loaded is a navigation, and the browser pushes a TOP-LEVEL history entry for
           it — so a shop whose URL is rebuilt once (every resolved shop, plus every
           section switch) left an entry behind that "back" landed on: same address, the
           iframe rolled back to the scan shown before it, no visible navigation. React
           replaces the element instead when the key changes, and a fresh iframe's first
           load replaces rather than pushes, so back goes where the user expects. */}
      {viewerUrl && (
        <iframe
          ref={iframeRef}
          key={`${viewerKind}:${viewerUrl}`}
          src={viewerUrl}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            border: 'none',
            display: 'block',
            pointerEvents: (reviewOpen || infoOpen || chatOpen || itemListOpen || cartDrawerOpen) ? 'none' : 'auto',
          }}
          allow="fullscreen"
          title="Shop Viewer"
        />
      )}

      {/* ── React overlays on top of iframe ── */}

      {/* Marked item circles + flags */}
      {frameCamera && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            zIndex: 100,
          }}
        >
          <FrameItemMarkers
            frameIndex={currentFrameIndex}
            frameCamera={frameCamera}
            frameDisplayWidth={displayWidth}
            frameDisplayHeight={displayHeight}
            shopId={shopId}
          />
        </div>
      )}

      {/* TapCameraIndicator — disk blocker + orbit + discovery */}
      {canUseTapIndicator && tapIndicator && tapIndicator.show && frameCamera && (
        <div style={{ position: 'absolute', inset: 0, zIndex: 200, pointerEvents: 'none' }}>
          <div style={{ position: 'relative', width: '100%', height: '100%', pointerEvents: 'none' }}>
            <TapCameraIndicator
              cameraCount={tapIndicator.cameraCount}
              position={indicatorPosition}
              currentDisplayX={indicatorDisplayX}
              currentDisplayY={indicatorDisplayY}
              frameRelativeTapX={tapIndicator.frameRelativeTapX}
              frameRelativeTapY={tapIndicator.frameRelativeTapY}
              imagePixelX={tapIndicator.imagePixelX}
              imagePixelY={tapIndicator.imagePixelY}
              frameCamera={frameCamera}
              frameIndex={currentFrameIndex}
              cameras={cameras}
              points3D={useAppStore.getState().points3D}
              offsetMap={offsetMap}
              frameDisplayWidth={displayWidth}
              radiusPxImageSpace={tapIndicator.is2DOnly ? undefined : tapIndicator.radiusPxImageSpace}
              scaleData={tapIndicator.is2DOnly ? undefined : tapIndicator.scaleData}
              zoomScale={1}
              onShowThumbnails={() => {}}
              onPanComplete={(_finalScreenX, _finalScreenY, finalDisplayX, finalDisplayY) => {
                setTapIndicatorPannedPosition({ x: finalDisplayX, y: finalDisplayY })
                // Convert display position back to image pixel for new discovery origin
                const cam = frameCamera
                const newImagePixelX = (finalDisplayX / displayWidth) * (cam?.intrinsics?.width ?? 450)
                const newImagePixelY = (finalDisplayY / displayHeight) * (cam?.intrinsics?.height ?? 800)
                setTapIndicator((prev) => prev ? { ...prev, imagePixelX: newImagePixelX, imagePixelY: newImagePixelY } : null)
              }}
              onClose={() => setTapIndicator(null)}
              shopId={shopId}
              is2DOnly={tapIndicator.is2DOnly}
              onMarkItem={() => {
                handleMarkItem({
                  imagePixelX: tapIndicator.imagePixelX,
                  imagePixelY: tapIndicator.imagePixelY,
                  radius: tapIndicator.is2DOnly ? 30 : (tapIndicator.radiusPxImageSpace ?? 30),
                  point3D: tapIndicator.point3D,
                  projections: tapIndicator.projections,
                  frameIndex: tapIndicator.frameIndex,
                })
                setTapIndicator(null)
                setItemListOpen(true)
              }}
              onOrbitPrev={handleOrbitPrev}
              onOrbitNext={handleOrbitNext}
            />
          </div>
        </div>
      )}

      {/* SectionsDrawer — section/room switching.
           Not gated on canUseTapIndicator any more. That flag means "points3d loaded, 3D
           discovery is possible", which the depth viewer never sets and never will — it is
           self-contained by design. Requiring it left v2, the DEFAULT viewer wherever depth
           exists, with no way to change section at all. Switching rooms has nothing to do with
           having a point cloud, and the remaining condition already needs a populated store. */}
      {!zenMode && (sections.length > 0 || shopDatasets.length > 1) && (
        <SectionsDrawer openSignal={drawerOpenSignal} />
      )}

      {/* "You are here", and the rooms either side — the visible twin of the doorway gesture. */}
      <SectionChip
        graph={sectionGraph}
        zenMode={zenMode}
        busy={!!crossing}
        frameIndex={currentFrameIndex}
        frameCount={viewerFrameCount || cameras.length}
        onStep={crossToSection}
        onOpenPicker={() => setDrawerOpenSignal((n) => n + 1)}
      />

      {/* Top-left floating toolbar (back, review, info, ask) — single flex row */}
      <div
        style={{
          position: 'fixed',
          top: 'calc(12px + env(safe-area-inset-top, 0px))',
          left: 12,
          zIndex: 600,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: 4,
          background: 'rgba(20,20,20,0.78)',
          border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: 14,
          backdropFilter: 'blur(10px)',
          boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
          opacity: zenMode ? 0 : 1,
          pointerEvents: zenMode ? 'none' : 'auto',
          transform: zenMode ? 'translateX(-18px) scale(0.9)' : 'none',
          transition: 'opacity 0.28s ease, transform 0.28s ease',
        }}
      >
        <button onClick={() => window.history.back()} style={toolbarBtn('#fff')} title="Back">‹</button>
        <span style={toolbarDivider} />
        {/* Viewer switch. Up to three viewers exist for a section and which ones depends on
            what has been generated for it, so this is a segmented control over exactly the
            available ones rather than a button that cycles: with three options, cycling makes
            the user press twice to reach a mode they can already see. A shop with only the
            classic viewer shows nothing here — a control with one option is not a control.

            Switching remounts the iframe (key={viewerKind}), so the frame index resets. */}
        {available.length > 1 ? (
          <>
            {available.map((kind) => (
              <button
                key={kind}
                onClick={() => {
                  if (kind === viewerKind) return
                  setViewerKind(kind)
                  setCurrentFrameIndex(0)
                  setTapIndicator(null)
                }}
                style={{
                  ...toolbarBtn(kind === viewerKind ? '#34d399' : '#9ca3af'),
                  ...toolbarPill,
                  background: kind === viewerKind ? 'rgba(52,211,153,0.14)' : 'transparent',
                }}
                aria-pressed={kind === viewerKind}
                title={VIEWER_TITLE[kind]}
              >
                {VIEWER_LABEL[kind]}
              </button>
            ))}
            <span style={toolbarDivider} />
          </>
        ) : authUser && cameras.length > 0 && scanIds ? (
          <>
            <button
              onClick={() => setDepthPromptOpen(true)}
              style={{ ...toolbarBtn('#9ca3af'), ...toolbarPill, fontWeight: 500 }}
              title="Build the depth view for this section on your device"
            >
              3D?
            </button>
            <span style={toolbarDivider} />
          </>
        ) : null}
        <button onClick={() => setReviewOpen(true)} style={toolbarBtn('#f59e0b')} title="Reviews">★</button>
        <button
          onClick={() => setInfoOpen(true)}
          style={{ ...toolbarBtn('#60a5fa'), fontStyle: 'italic', fontFamily: 'serif', fontWeight: 700 }}
          title="Shop info"
        >i</button>
        <button
          onClick={() => setAskPin({ x: displayWidth / 2, y: displayHeight / 2, r: 60 })}
          style={{
            ...toolbarBtn('#34d399'),
            display: askPin || chatOpen ? 'none' : 'flex',
          }}
          title="Ask about something in this view"
        >?</button>
        {showFrameDownload && (
          <>
            <span style={toolbarDivider} />
            <button
              onClick={handleDownloadFrames}
              disabled={!!frameDownload}
              style={{
                ...toolbarBtn('#a78bfa'),
                width: frameDownload ? 'auto' : 38,
                padding: frameDownload ? '0 8px' : 0,
                fontSize: frameDownload ? 11 : 18,
                fontWeight: frameDownload ? 700 : 400,
                cursor: frameDownload ? 'default' : 'pointer',
                opacity: frameDownload ? 0.75 : 1,
              }}
              title="Dev only — download this section's frames as a .zip"
            >
              {frameDownload
                ? (frameDownload.total ? `${frameDownload.done}/${frameDownload.total}` : '…')
                : '⤓'}
            </button>
          </>
        )}
      </div>

      {/* Floating bag button — below back arrow, top-left */}
      {visibleItemCount > 0 && (
        <button
          onClick={() => setItemListOpen(true)}
          style={{
            position: 'fixed',
            top: 'calc(72px + env(safe-area-inset-top, 0px))',
            left: 16,
            zIndex: 600,
            background: 'rgba(20,20,20,0.85)',
            border: '1.5px solid rgba(255,255,255,0.25)',
            borderRadius: 12,
            color: '#fff',
            width: 44,
            height: 44,
            display: 'flex',
            alignItems: 'center',
            opacity: zenMode ? 0 : 1,
            pointerEvents: zenMode ? 'none' : 'auto',
            transform: zenMode ? 'translateX(-18px) scale(0.88)' : 'none',
            transition: 'opacity 0.28s ease, transform 0.28s ease',
            justifyContent: 'center',
            gap: 4,
            fontSize: 18,
            cursor: 'pointer',
            backdropFilter: 'blur(8px)',
            boxShadow: '0 2px 12px rgba(0,0,0,0.5)',
          }}
          title="View marked items"
        >
          🛍️
          <span style={{ fontSize: 12, fontWeight: 700 }}>{visibleItemCount}</span>
        </button>
      )}

      {/* Asset target circle — read-only highlight when opened via a chat asset chip */}
      {assetPin && frameCamera && currentFrameIndex === assetPin.frame && (() => {
        const imgW = frameCamera.intrinsics?.width ?? displayWidth
        const imgH = frameCamera.intrinsics?.height ?? displayHeight
        const cx = (assetPin.px / imgW) * displayWidth
        const cy = (assetPin.py / imgH) * displayHeight
        const r = (assetPin.pr / imgW) * displayWidth
        return (
          <>
            <div
              style={{
                position: 'fixed',
                left: cx - r,
                top: cy - r,
                width: r * 2,
                height: r * 2,
                borderRadius: '50%',
                border: '3px solid #34d399',
                boxShadow: '0 0 22px rgba(52,211,153,0.7)',
                background: 'rgba(52,211,153,0.10)',
                pointerEvents: 'none',
                zIndex: 590,
              }}
            />
            <button
              onClick={() => setAssetPin(null)}
              style={{
                position: 'fixed',
                left: cx + r + 6,
                top: cy - r - 6,
                width: 26, height: 26,
                borderRadius: '50%',
                background: 'rgba(20,20,20,0.85)',
                border: '1.5px solid rgba(52,211,153,0.6)',
                color: '#86efac',
                fontSize: 14,
                cursor: 'pointer',
                zIndex: 595,
                lineHeight: 1,
                padding: 0,
              }}
              title="Hide asset highlight"
            >✕</button>
          </>
        )
      })()}

      {/* The crossing itself. Switching sections changes the viewer URL, which changes the
          iframe's key, which tears the document down and builds a new one — several hundred ms
          of black between the room you left and the room you are in. This covers it with the
          room you are going to, so the crossing is continuous with the doorway that started it.
          z-610: above the chip and the toolbar, below AskPinOverlay. */}
      {crossing && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 610,
            background: '#05070a',
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
            pointerEvents: 'auto',
          }}
        >
          {crossing.poster && (
            <img
              src={crossing.poster}
              alt=""
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                animation: 'morph-crossing-in 380ms cubic-bezier(0.4, 0, 0.2, 1) both',
              }}
            />
          )}
          <div
            style={{
              position: 'relative',
              marginBottom: 'calc(11% + env(safe-area-inset-bottom, 0px))',
              padding: '7px 16px',
              background: 'rgba(20,20,20,0.78)',
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: 22,
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              color: '#fff',
              fontSize: 13,
              fontWeight: 600,
              letterSpacing: '.01em',
              boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
            }}
          >{crossing.name}</div>
        </div>
      )}

      {/* AskPin overlay — draggable + resizable circle */}
      {askPin && frameCamera && (
        <AskPinOverlay
          pin={askPin}
          setPin={setAskPin}
          displayWidth={displayWidth}
          displayHeight={displayHeight}
          onCancel={() => setAskPin(null)}
          onConfirm={() => {
            const imgW = frameCamera.intrinsics?.width ?? displayWidth
            const imgH = frameCamera.intrinsics?.height ?? displayHeight
            const imagePixelX = (askPin.x / displayWidth) * imgW
            const imagePixelY = (askPin.y / displayHeight) * imgH
            const radius = (askPin.r / displayWidth) * imgW
            const idx = shopDatasets.findIndex((d: any) => String(d.sectionId) === String(currentSectionId))
            setChatPin({
              frameIndex: currentFrameIndex,
              imagePixelX,
              imagePixelY,
              radius,
              sectionIndex: idx < 0 ? 0 : idx,
            })
            setAskPin(null)
            setChatOpen(true)
          }}
        />
      )}

      {reviewOpen && (
        <ShopReviewDrawer
          shopId={shopId}
          onClose={() => setReviewOpen(false)}
        />
      )}

      {infoOpen && (
        <ShopInfoDrawer
          shopId={shopId}
          onClose={() => setInfoOpen(false)}
          onChat={() => setChatOpen(true)}
        />
      )}

      {chatOpen && (
        <ChatBalloon
          shopId={shopId}
          frameIndex={chatPin?.frameIndex ?? currentFrameIndex}
          imagePixelX={chatPin?.imagePixelX}
          imagePixelY={chatPin?.imagePixelY}
          radius={chatPin?.radius}
          sectionIndex={chatPin?.sectionIndex}
          initialPin={chatPin ? {
            datasetId: shopId,
            sectionIndex: chatPin.sectionIndex,
            frameIndex: chatPin.frameIndex,
            imagePixelX: chatPin.imagePixelX,
            imagePixelY: chatPin.imagePixelY,
            radius: chatPin.radius,
          } : null}
          anchorX={displayWidth / 2}
          anchorY={displayHeight / 2}
          viewportWidth={displayWidth}
          viewportHeight={displayHeight}
          userId={authUser?.id ?? null}
          onClose={() => { setChatOpen(false); setChatPin(null) }}
        />
      )}

      {/* Marked items panel + cart drawer */}
      <ItemListPanel
        isOpen={itemListOpen}
        onClose={() => setItemListOpen(false)}
        onOpenCart={() => { setItemListOpen(false); setCartDrawerOpen(true) }}
        shopId={shopId}
      />
      <CartDrawer isOpen={cartDrawerOpen} onClose={() => setCartDrawerOpen(false)} />

      {/* Depth generation — mounted only while open, so the ~50 MB of runtime it lazily
          imports is never touched by a visitor who does not ask for it. */}
      {depthPromptOpen && scanLocation && scanIds && (
        <DepthGeneratePrompt
          // The ids the frames are actually stored under, not the display index -- see scanIds.
          shopId={scanIds.shopId}
          sectionId={scanIds.sectionId}
          // shopDatasets, not `sections`: the store's `sections` are DETECTED frame groups
          // (disconnected components of the scan), while the shop's named sections — the ones
          // the SectionsDrawer shows and the user would recognise — live in shopDatasets.
          sectionName={shopDatasets.find(
            (d: any) => String(d.sectionId) === String(currentSectionId),
          )?.sectionName}
          frameCount={cameras.length}
          frameUrl={(name) =>
            `${scanLocation.base}frames/${name}${scanLocation.sas ? `?${scanLocation.sas}` : ''}`}
          onClose={() => setDepthPromptOpen(false)}
          onGenerated={() => { refreshDepth(); setCurrentFrameIndex(0); setTapIndicator(null) }}
        />
      )}
    </div>
  )
}

export default MorphViewerPage

const toolbarBtn = (color: string): React.CSSProperties => ({
  width: 38,
  height: 38,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'transparent',
  border: 'none',
  borderRadius: 10,
  color,
  fontSize: 20,
  cursor: 'pointer',
  padding: 0,
  WebkitTapHighlightColor: 'transparent',
})

/**
 * What each viewer is called in the toolbar, and what tapping it gets you.
 *
 * Named for what the visitor sees, not the data behind it: "Scene", not "DepthwithColmap",
 * because nobody browsing a shop knows or should have to know that a fused point cloud is what
 * makes the room walkable.
 */
const VIEWER_LABEL: Record<ViewerKind, string> = { v1: '2D', v2: '3D', v3: 'Scene' }
const VIEWER_TITLE: Record<ViewerKind, string> = {
  v1: 'Classic view — the photographs, morphed between',
  v2: 'Depth view — each frame given real depth',
  v3: 'Scene — the whole section as one 3D space you can move around in',
}

/** The viewer switch is a short word, not an icon, so it needs width the icon buttons don't. */
const toolbarPill: React.CSSProperties = {
  width: 'auto',
  padding: '0 9px',
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: '.02em',
}

const toolbarDivider: React.CSSProperties = {
  width: 1,
  height: 22,
  background: 'rgba(255,255,255,0.15)',
  margin: '0 2px',
}
